import * as React from 'react';
import { useEffect, useState } from 'react';
import { floatToString, getNumberOfDecimals, getNumberOfIntegerDigits, isFloat, replaceSeparatorWithDot } from 'ui/numberbox/utils';

export interface SmallNumberboxProps {
  className?: string;
  value: number | '';
  fraction: number;
  separator: string;
  onBlur: (evt: React.SyntheticEvent<HTMLInputElement>) => void;
  onChange: (evt: React.SyntheticEvent<HTMLInputElement>, modelValue: number) => void;
  integer: number;
  positive: boolean;
  negative: boolean;
  disabled: boolean;
  readOnly: boolean;
}

export const SmallNumberbox = ({
  className,
  value,
  fraction: maxDecimals,
  separator = '.',
  onBlur,
  onChange,
  integer,
  positive: isPositiveAllowed,
  negative: isNegativeAllowed,
  disabled,
  readOnly
}: SmallNumberboxProps) => {

  const convertValueToString = (val: number | string): string => {
    const stringValue = String(val);
    // tslint:disable-next-line: no-unnecessary-local-variable
    const valueConvertedToString: string = isFloat(val) && stringValue?.slice(-1) !== '.' ?
      floatToString(Number(val), separator, maxDecimals)
      :
      stringValue;
    return valueConvertedToString;
  };

  const handleOnChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const currentValue: string = e?.currentTarget?.value;
    const currentValueWithSeparatorReplacedByDot: string = replaceSeparatorWithDot(currentValue, separator);
    const exceedsMaxDecimals: boolean = getNumberOfDecimals(currentValueWithSeparatorReplacedByDot) > maxDecimals;
    const includesMinusCharacter: boolean = currentValueWithSeparatorReplacedByDot?.includes('-');
    const includesMinusCharacterWhenNegativeNumbersNotAllowed: boolean = !isNegativeAllowed && includesMinusCharacter;
    const missingMinusCharacterWhenPositiveNumbersNotAllowed: boolean = !isPositiveAllowed && !includesMinusCharacter;
    const isWrongSeparator: boolean = separator !== '.' && 
      isFloat(currentValueWithSeparatorReplacedByDot) && 
      currentValue.indexOf(separator) === -1;
    const exceedsMaxDigitsOfIntegerPart: boolean = getNumberOfIntegerDigits(currentValueWithSeparatorReplacedByDot) > integer;
    const isMinusCharacter: boolean = isNegativeAllowed && currentValueWithSeparatorReplacedByDot === '-';
    // accept only input values that can be converted to a number or a single minus character ("-");
    const isInvalidInput: boolean = !isMinusCharacter && isNaN(Number(currentValueWithSeparatorReplacedByDot));

    if (isInvalidInput ||
      exceedsMaxDecimals ||
      includesMinusCharacterWhenNegativeNumbersNotAllowed ||
      missingMinusCharacterWhenPositiveNumbersNotAllowed ||
      exceedsMaxDigitsOfIntegerPart ||
      isWrongSeparator) {
      return;
    }
    // set the viewValue as the input field's value
    setViewValue(currentValue);
    // convert the string to a number and set it as the numberbox component's value
    onChange(e, Number(currentValueWithSeparatorReplacedByDot));
  };

  const handleOnBlur = (evt: React.SyntheticEvent<HTMLInputElement>) => {
    const currentValue: string = evt?.currentTarget?.value;
    // Convert and format the current value - remove trailing zeros, change "0.00" to "0" etc.
    setViewValue(convertValueToString(replaceSeparatorWithDot(currentValue, separator)));
    onBlur(evt);
  };

  const [viewValue, setViewValue] = useState('');

  useEffect( () => {
    setViewValue(convertValueToString(value));
  }, [value]);

  useEffect( () => {
    setViewValue(convertValueToString(value));
  }, []);

  return <input className={className} type="text" value={viewValue}
    onChange={handleOnChange} onBlur={handleOnBlur} readOnly={readOnly} disabled={disabled} />;
};
